import {
  Paper,
  Stack,
  Typography,
  Box,
  Switch,
  FormControlLabel,
  Checkbox,
  Grid,
  Radio,
  RadioGroup,
  FormControl,
  Collapse,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useMemo, useEffect } from "react";
import { UITooltip } from "../../../components/UITooltip";
import { VersionWarningTooltip } from "../components/settings/VersionWarningTooltip";
import {
  FEATURE_CONFIG,
  DEFAULT_COUNTRY_MATCHING_FIELD,
  FALLBACK_MESSAGES,
  AUTO_SYNC,
  FIELD_LABELS,
} from "../components/settings/constants";
import { useSalesforceIntegrationDetailsContext } from "../contexts/useSalesforceIntegrationDetailsContext";
import { SFDCIntegration } from "../types";

// Custom hook for feature version checks
const useFeatureVersionChecks = (integration: SFDCIntegration) => {
  return useMemo(() => {
    const version = integration?.managedPackageCurrentVersion;
    const checks: Record<string, boolean> = {};

    // Check recordTypes
    checks.recordTypes = FEATURE_CONFIG.recordTypes.versionCheck(version);

    // Check each HG Data Enrichment field individually
    FEATURE_CONFIG.hgDataEnrichment.fields.forEach((field) => {
      if (field.versionCheck) {
        checks[field.name] = field.versionCheck(version);
      }
    });

    // Check other features
    checks.localizedInstallation =
      FEATURE_CONFIG.localizedInstallation.versionCheck(version);
    checks.countryLevelMatching =
      FEATURE_CONFIG.countryLevelMatching.versionCheck(version);
    checks.hgIdMatching = FEATURE_CONFIG.hgIdMatching.versionCheck(version);

    return checks;
  }, [integration?.managedPackageCurrentVersion]);
};

// Reusable HelpIcon component
const HelpIcon = ({ title }: { title: string }) => (
  <UITooltip title={title} placement="top">
    <HelpOutlineIcon
      sx={{
        fontSize: 18,
        color: "text.secondary",
        cursor: "help",
        "&:hover": {
          color: "primary.main",
        },
      }}
    />
  </UITooltip>
);

// Reusable CheckboxField component
const CheckboxField = ({
  label,
  checked,
  onChange,
  isAllowed,
  sx,
}: {
  label: string;
  checked: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isAllowed: boolean;
  sx?: object;
}) => (
  <Box display="flex" alignItems="center" gap={1}>
    <FormControlLabel
      control={
        <Checkbox
          checked={isAllowed ? checked : false}
          disabled={!isAllowed}
          onChange={onChange}
          size="small"
          sx={sx}
          disableRipple
        />
      }
      label={label}
      sx={{
        marginRight: 0,
      }}
    />
    <VersionWarningTooltip isAllowed={isAllowed} />
  </Box>
);

// Reusable SwitchField component
const SwitchField = ({
  title,
  tooltip,
  description,
  checked,
  onChange,
  isAllowed,
  children,
  "data-testid": dataTestId,
}: {
  title: string;
  tooltip: string;
  description?: string;
  checked: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isAllowed: boolean;
  children?: React.ReactNode;
  "data-testid"?: string;
}) => (
  <Box>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Box flex={1} display="flex" alignItems="center" gap={1}>
        <Typography fontWeight="500">{title}</Typography>
        <HelpIcon title={tooltip} />
      </Box>
      <Box display="flex" alignItems="center" gap={1}>
        <VersionWarningTooltip isAllowed={isAllowed} />
        <Switch
          data-testid={dataTestId}
          checked={isAllowed ? checked : false}
          disabled={!isAllowed}
          onChange={onChange}
          color="primary"
          size="small"
          disableRipple
        />
      </Box>
    </Stack>
    {description && (
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    )}
    {children}
  </Box>
);

export const SalesforceSettingsView = () => {
  const { integration, draftSettings, updateDraftSetting } =
    useSalesforceIntegrationDetailsContext();

  // Feature version checks
  const featureChecks = useFeatureVersionChecks(integration!);

  // Auto-uncheck disabled features when version doesn't support them
  useEffect(() => {
    if (!draftSettings) return;

    // Generic handling for most features (excluding hgDataEnrichment)
    Object.entries(FEATURE_CONFIG).forEach(([featureKey, config]) => {
      if (featureKey !== "hgDataEnrichment") {
        const isAllowed = featureChecks[featureKey];
        if (!isAllowed) {
          config.fields.forEach((field) => {
            if (draftSettings[field.name]) {
              updateDraftSetting(field.name, false);
            }
          });
        }
      }
    });

    // Special handling for HG Data Enrichment
    FEATURE_CONFIG.hgDataEnrichment.fields.forEach((field) => {
      if (!featureChecks[field.name] && draftSettings[field.name]) {
        updateDraftSetting(field.name, false);
      }
    });
  }, [featureChecks, draftSettings, updateDraftSetting]);

  // Styling for disabled checkboxes
  const disabledCheckboxSx = {
    "& .MuiCheckbox-root.Mui-disabled": {
      color: "rgba(0, 0, 0, 0.26)",
    },
    "& .MuiFormControlLabel-label.Mui-disabled": {
      color: "rgba(0, 0, 0, 0.38)",
    },
  };

  const hgDataDisabledCheckboxSx = {
    "&.Mui-disabled": {
      opacity: 1,
      "& .MuiCheckbox-root": {
        color: "#9e9e9e",
      },
      "& .MuiTypography-root": {
        color: "#9e9e9e",
      },
    },
  };

  // Event handlers
  const handleAutoSyncChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateDraftSetting("autoSync", event.target.checked);
  };

  const handleLocalizedInstallationChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateDraftSetting("localizeInstalls", event.target.checked);
  };

  const handleCountryLevelMatchingChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateDraftSetting("useCountryLevelMatching", event.target.checked);
    // Set default country field when enabling
    if (event.target.checked && !draftSettings?.accountsCountryField) {
      updateDraftSetting(
        "accountsCountryField",
        DEFAULT_COUNTRY_MATCHING_FIELD
      );
    }
  };

  const handleCountryFieldChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateDraftSetting("accountsCountryField", event.target.value);
  };

  const handleHGIDMatchingChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateDraftSetting("customHgIdMatching", event.target.checked);
  };

  if (!integration || !draftSettings) {
    return (
      <Stack gap={2} sx={{ maxWidth: 600, mx: "auto", p: 3 }}>
        <Typography variant="h4" textAlign="center">
          {FALLBACK_MESSAGES.configureSettingsTitle}
        </Typography>
        <Paper sx={{ p: 3 }}>
          <Typography>{FALLBACK_MESSAGES.noIntegrationFound}</Typography>
        </Paper>
      </Stack>
    );
  }

  return (
    <Stack spacing={1}>
      {/* Auto Sync HG with Salesforce */}
      <SwitchField
        data-testid="salesforce-autosync-switch"
        title={AUTO_SYNC.title}
        tooltip={AUTO_SYNC.tooltip}
        description={
          integration.environment === "SANDBOX"
            ? AUTO_SYNC.sandboxLimitationMessage
            : undefined
        }
        checked={draftSettings.autoSync}
        onChange={handleAutoSyncChange}
        isAllowed
      />

      {/* Record Types */}
      <Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography fontWeight="500">
            {FEATURE_CONFIG.recordTypes.title}
          </Typography>
          <HelpIcon title={FEATURE_CONFIG.recordTypes.tooltip} />
        </Box>
        <Grid container>
          {FEATURE_CONFIG.recordTypes.fields.map((field) => (
            <Grid key={field.name} size={6}>
              <CheckboxField
                label={field.label}
                checked={draftSettings[field.name]}
                onChange={(event) =>
                  updateDraftSetting(field.name, event.target.checked)
                }
                isAllowed={featureChecks.recordTypes}
                sx={disabledCheckboxSx}
              />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* HG Data Enrichment */}
      <Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography fontWeight="500">
            {FEATURE_CONFIG.hgDataEnrichment.title}
          </Typography>
          <HelpIcon title={FEATURE_CONFIG.hgDataEnrichment.tooltip} />
        </Box>
        <Grid container>
          {FEATURE_CONFIG.hgDataEnrichment.fields.map((field) => (
            <Grid key={field.name} size={6}>
              <CheckboxField
                label={field.label}
                checked={draftSettings[field.name]}
                onChange={(event) =>
                  updateDraftSetting(field.name, event.target.checked)
                }
                isAllowed={featureChecks[field.name]}
                sx={hgDataDisabledCheckboxSx}
              />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Localized Installation */}
      <SwitchField
        title={FEATURE_CONFIG.localizedInstallation.title}
        tooltip={FEATURE_CONFIG.localizedInstallation.tooltip}
        description={FEATURE_CONFIG.localizedInstallation.description}
        checked={draftSettings.localizeInstalls}
        onChange={handleLocalizedInstallationChange}
        isAllowed={featureChecks.localizedInstallation}
      />

      {/* Country Level Matching */}
      <SwitchField
        data-testid="salesforce-country-level-switch"
        title={FEATURE_CONFIG.countryLevelMatching.title}
        tooltip={FEATURE_CONFIG.countryLevelMatching.tooltip}
        description={FEATURE_CONFIG.countryLevelMatching.description}
        checked={draftSettings.useCountryLevelMatching}
        onChange={handleCountryLevelMatchingChange}
        isAllowed={featureChecks.countryLevelMatching}
      >
        <Collapse in={draftSettings.useCountryLevelMatching} unmountOnExit>
          <FormControl component="fieldset">
            <RadioGroup
              value={draftSettings.accountsCountryField || "BillingCountry"}
              onChange={handleCountryFieldChange}
              row
            >
              <FormControlLabel
                value="BillingCountry"
                control={<Radio size="small" />}
                label={FIELD_LABELS.billingCountry}
              />
              <FormControlLabel
                value="ShippingCountry"
                control={<Radio size="small" />}
                label={FIELD_LABELS.shippingCountry}
              />
            </RadioGroup>
          </FormControl>
        </Collapse>
      </SwitchField>

      {/* HG ID Matching */}
      <SwitchField
        data-testid="salesforce-hgid-matching-switch"
        title={FEATURE_CONFIG.hgIdMatching.title}
        tooltip={FEATURE_CONFIG.hgIdMatching.tooltip}
        description={FEATURE_CONFIG.hgIdMatching.description}
        checked={draftSettings.customHgIdMatching}
        onChange={handleHGIDMatchingChange}
        isAllowed={featureChecks.hgIdMatching}
      />
    </Stack>
  );
};
